import React from 'react';
import { Network, Plus, X, Edit2, Trash2, Loader, Globe, Shield } from 'lucide-react';
import type { SubnetData } from './SubnetModal';

interface SubnetListProps {
    subnets: SubnetData[];
    isLoading: boolean;
    onAddNew: () => void;
    onEdit: (subnet: SubnetData) => void;
    onDelete: (subnetId: string) => void;
    onClose: () => void;
}

const SubnetList: React.FC<SubnetListProps> = ({
    subnets,
    isLoading,
    onAddNew,
    onEdit,
    onDelete,
    onClose
}) => {
    const handleDelete = (subnet: SubnetData) => {
        if (window.confirm(`Are you sure you want to delete subnet "${subnet.name}"?`)) {
            onDelete(subnet.id!);
        }
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                    <div className="mr-3 text-orange-400">
                        <Network className="h-6 w-6" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Subnet Configuration</h2>

                    <div className='ml-1'>
                        <button
                            onClick={onAddNew}
                            className="flex items-center gap-2 px-2 py-1 bg-gradient-to-r from-orange-600 to-orange-700 
                        text-white rounded-lg font-semibold hover:from-orange-700 hover:to-orange-800 
                        hover:shadow-lg hover:shadow-orange-500/20 transition-all duration-200"
                        >
                            <Plus className="w-4 h-4" />
                            Add 
                        </button>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-800"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Add New Button */}
            {/* <div className="mb-4">
                <button
                    onClick={onAddNew}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 
                        text-white rounded-lg font-semibold hover:from-orange-700 hover:to-orange-800 
                        hover:shadow-lg hover:shadow-orange-500/20 transition-all duration-200"
                >
                    <Plus className="w-4 h-4" />
                    Add New Subnet
                </button>
            </div> */}

            {/* List Content */}
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader className="w-8 h-8 text-orange-400 animate-spin" />
                    <span className="ml-3 text-gray-400">Loading subnets...</span>
                </div>
            ) : subnets.length === 0 ? (
                <div className="text-center py-12">
                    <Network className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg mb-2">No subnets configured</p>
                    <p className="text-gray-500 text-sm">Click "Add New Subnet" to get started</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {subnets.map((subnet) => (
                        <div
                            key={subnet.id}
                            className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 hover:border-orange-400/30 transition-all duration-200"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    {/* Name and Status */}
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-base font-semibold text-white">{subnet.name}</h3>
                                        <div className="flex items-center gap-1">
                                            <Globe className="w-3.5 h-3.5 text-blue-400" />
                                            <span className="text-gray-300 font-mono text-xs">{subnet.subnet}</span>
                                        </div>
                                        <span
                                            className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${subnet.enabled
                                                ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                                                : 'bg-gray-600/20 text-gray-400 border border-gray-600/30'
                                                }`}
                                        >
                                            {subnet.enabled ? 'Enabled' : 'Disabled'}
                                        </span>
                                    </div>

                                    {/* Subnet */}


                                    {/* Description */}
                                    {subnet.description && (
                                        <p className="text-gray-400 text-xs mb-2 leading-snug">{subnet.description}</p>
                                    )}

                                    {/* Exclude IPs */}
                                    {subnet.excludeIPs && subnet.excludeIPs.length > 0 && (
                                        <div className="mb-1">
                                            <div className="flex items-center gap-1 mb-0.5">
                                                <Shield className="w-3 h-3 text-yellow-400" />
                                                <span className="text-[11px] text-gray-400">Excluded IPs:</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {subnet.excludeIPs.map((ip, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="px-1.5 py-0.5 bg-yellow-600/20 text-yellow-300 text-[11px] rounded border border-yellow-600/30 font-mono"
                                                    >
                                                        {ip}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Ports */}
                                    {subnet.ports && subnet.ports.length > 0 && (
                                        <div>
                                            <div className="flex items-center gap-1 mb-0.5">
                                                <Network className="w-3 h-3 text-indigo-400" />
                                                <span className="text-[11px] text-gray-400">Ports:</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {subnet.ports.map((port, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="px-1.5 py-0.5 bg-indigo-600/20 text-indigo-300 text-[11px] rounded border border-indigo-600/30 font-mono"
                                                    >
                                                        {port}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-1.5 ml-2">
                                    <button
                                        onClick={() => onEdit(subnet)}
                                        className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 rounded-lg transition-all duration-200"
                                        title="Edit subnet"
                                    >
                                        <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(subnet)}
                                        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-all duration-200"
                                        title="Delete subnet"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                    ))}
                </div>
            )}
        </div>
    );
};

export default SubnetList;
